import { useState, useEffect, useCallback } from 'react';
import { LogoMark } from '../../components/LogoMark';
import { useNavigate, Link } from 'react-router-dom';
import { LogIn, Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { login, getSocialOAuthRedirectUrl } from '../../api/auth';
import { useAuth } from '../../contexts/AuthContext';
import { useBackendHealth } from '../../hooks/useBackendHealth';
import { humanizeAuthError } from '../../utils/authErrors';
import { prefetchDashboardData } from '../../utils/prefetchDashboard';
import { PasswordInput } from '../../components/ui/PasswordInput';

const SLEEPING_BACKEND_HINT = "Server isn't ready yet — still waking up...";

type PendingAction = null | 'login' | 'google' | 'github';

export function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refetch, setUserFromLogin } = useAuth();
  const { isBackendReady, retryCount, checkNow } = useBackendHealth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);

  const isInIframe = window.self !== window.top;

  // When in an iframe, refetch auth on focus (user may have logged in via popup)
  useEffect(() => {
    if (!isInIframe) return;
    const onFocus = async () => {
      await refetch();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [isInIframe, refetch]);

  // When in an iframe (e.g. extension side panel), the OAuth popup tab signals
  // login completion two ways:
  //   1. window.opener.postMessage — crosses the extension-iframe storage
  //      partition that BroadcastChannel cannot cross.
  //   2. BroadcastChannel — works between same-origin sibling tabs (e.g. two
  //      regular web tabs).
  useEffect(() => {
    if (!isInIframe) return;

    const onLogin = async () => {
      await refetch();
      prefetchDashboardData(queryClient);
      navigate('/app');
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'login-complete') return;
      void onLogin();
    };
    window.addEventListener('message', onMessage);

    let bc: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== 'undefined') {
      bc = new BroadcastChannel('danny-auth');
      bc.onmessage = (event) => {
        if (event.data?.type !== 'login-complete') return;
        void onLogin();
      };
    }

    return () => {
      window.removeEventListener('message', onMessage);
      bc?.close();
    };
  }, [isInIframe, refetch, navigate, queryClient]);

  const executeLogin = useCallback(async () => {
    try {
      const res = await login({ email, password });
      if (res?.user) {
        setUserFromLogin(res.user);
      } else {
        await refetch();
      }
      prefetchDashboardData(queryClient);
      navigate('/app');
    } catch (err: unknown) {
      setError(
        humanizeAuthError(err, 'Invalid email or password', { networkMessage: SLEEPING_BACKEND_HINT }),
      );
    } finally {
      setIsLoading(false);
    }
  }, [email, password, setUserFromLogin, refetch, navigate, queryClient]);

  const executeOAuth = useCallback(async (provider: 'google' | 'github') => {
    try {
      const url = await getSocialOAuthRedirectUrl(provider);
      if (isInIframe) {
        window.open(url, '_blank');
        setIsLoading(false);
      } else {
        window.location.href = url;
      }
    } catch (err: unknown) {
      setError(
        humanizeAuthError(err, `Failed to start ${provider} login`, { networkMessage: SLEEPING_BACKEND_HINT }),
      );
      setIsLoading(false);
    }
  }, [isInIframe]);

  // When backend becomes ready and there's a pending action, auto-fire it
  // No timeout — we wait patiently. The health hook polls with backoff.
  useEffect(() => {
    if (!isBackendReady || !pendingAction) return;

    setError(null); // Clear any previous "not ready" errors
    const action = pendingAction;
    setPendingAction(null);

    if (action === 'login') {
      executeLogin();
    } else {
      executeOAuth(action);
    }
  }, [isBackendReady, pendingAction, executeLogin, executeOAuth]);

  // When user clicks login/OAuth and backend isn't ready, kick the health check
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('Please enter both email and password');
      return;
    }

    setError(null);
    setIsLoading(true);

    if (isBackendReady) {
      await executeLogin();
    } else {
      // Queue the action — it fires automatically when health check passes
      setPendingAction('login');
      checkNow(); // Kick an immediate health check
    }
  };

  const handleOAuthLogin = (provider: 'google' | 'github') => {
    setError(null);
    setIsLoading(true);

    if (isBackendReady) {
      executeOAuth(provider);
    } else {
      setPendingAction(provider);
      checkNow();
    }
  };

  // Cancel pending action
  const handleCancel = () => {
    setPendingAction(null);
    setIsLoading(false);
    setError(null);
  };

  const isWaking = pendingAction !== null;

  // Friendly waking message with attempt count
  const wakingMessage = retryCount === 0
    ? 'Waking up Danny...'
    : retryCount < 3
      ? `Waking up Danny... (hang tight, ${retryCount + 1} checks)`
      : `Still waking up... (${retryCount + 1} checks — cold starts can take up to 30s)`;

  return (
    <div className="min-h-screen bg-surface-base-light dark:bg-surface-base-dark flex items-center justify-center px-4 pt-safe">
      <div className="w-full max-w-md">
        <div className="bg-surface-raised-light dark:bg-surface-raised-dark rounded-xl shadow-card border border-border-subtle-light dark:border-border-subtle-dark p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <LogoMark className="h-10 w-auto" />
          </div>

          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
              Welcome back
            </h1>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
              Sign in to continue to Danny
            </p>
          </div>

          {/* Error/status messages */}
          {error && !isWaking && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm rounded-lg">
              {error}
            </div>
          )}

          {/* Waking up banner — shows while waiting for backend */}
          {isWaking && (
            <div className="mb-4 p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-300 text-sm rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{wakingMessage}</span>
              </div>
              <button
                onClick={handleCancel}
                className="text-xs underline opacity-70 hover:opacity-100"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Login form */}
          <form onSubmit={handleSubmit} className="space-y-4 mb-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full px-3 py-2.5 bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-primary-500 text-base"
                disabled={isWaking}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1">
                Password
              </label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full px-3 py-2.5 bg-surface-base-light dark:bg-surface-base-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark focus:outline-none focus:ring-2 focus:ring-primary-500 text-base"
                disabled={isWaking}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-700 hover:bg-primary-800 text-white font-medium rounded-lg shadow-subtle hover:shadow-hover dark:glow-primary dark:hover:glow-primary-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isWaking && pendingAction === 'login' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {wakingMessage}
                </>
              ) : isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4" />
                  Sign In
                </>
              )}
            </button>
          </form>

          {/* OAuth buttons */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border-subtle-light dark:border-border-subtle-dark"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-surface-raised-light dark:bg-surface-raised-dark text-text-secondary-light dark:text-text-secondary-dark">or</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => handleOAuthLogin('google')}
              disabled={isLoading}
              className="flex items-center justify-center gap-2 px-4 py-2.5 border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isWaking && pendingAction === 'google' ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
              )}
              Google
            </button>
            <button
              onClick={() => handleOAuthLogin('github')}
              disabled={isLoading}
              className="flex items-center justify-center gap-2 px-4 py-2.5 border border-border-subtle-light dark:border-border-subtle-dark rounded-lg text-text-primary-light dark:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isWaking && pendingAction === 'github' ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.87 8.17 6.84 9.5.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.87 1.52 2.34 1.07 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92 0-1.11.38-2 1.03-2.71-.1-.25-.45-1.29.1-2.64 0 0 .84-.27 2.75 1.02.79-.22 1.65-.33 2.5-.33.85 0 1.71.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.35.2 2.39.1 2.64.65.71 1.03 1.6 1.03 2.71 0 3.82-2.34 4.66-4.57 4.91.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0012 2z"/>
                </svg>
              )}
              GitHub
            </button>
          </div>

          {/* Links */}
          <div className="mt-6 text-center space-y-2">
            <Link to="/forgot-password" className="block text-sm text-primary-600 dark:text-primary-400 hover:underline">
              Forgot password?
            </Link>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary-600 dark:text-primary-400 hover:underline">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
